import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Box,
  Alert,
  Stack,
  Chip,
  OutlinedInput
} from '@mui/material';
import { Add, EmojiEvents } from '@mui/icons-material';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { drawService } from '../services/drawService';
import { campaignService } from '../services/campaignService';
import { prizeService } from '../services/prizeService';
import { Campaign, Prize } from '../types';
import { AppHeader } from '../components/AppHeader';
import { useNavigate } from 'react-router-dom';

interface DrawFormData {
  campaignId: string;
  prizeIds: string[];
  drawDate: string;
  maxWinners: number;
  allowMultiplePrizesPerUser: boolean;
  allowMultipleWinnersPerStore: boolean;
}

const drawSchema: yup.ObjectSchema<DrawFormData> = yup.object({
  campaignId: yup.string().required('Campanha é obrigatória'),
  prizeIds: yup.array()
    .of(yup.string().required())
    .min(1, 'Selecione pelo menos um prêmio')
    .required('Prêmios são obrigatórios'),
  drawDate: yup.string().required('Data do sorteio é obrigatória'),
  maxWinners: yup.number()
    .required('Quantidade máxima de ganhadores é obrigatória')
    .min(1, 'Deve ser no mínimo 1')
    .integer('Deve ser um número inteiro'),
  allowMultiplePrizesPerUser: yup.boolean().required(),
  allowMultipleWinnersPerStore: yup.boolean().required()
});

  const DrawCreate: React.FC = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [prizes, setPrizes] = useState<Prize[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const { control, handleSubmit, watch, formState: { errors } } = useForm<DrawFormData>({
    resolver: yupResolver(drawSchema),
    defaultValues: {
      campaignId: '',
      prizeIds: [],
      drawDate: '',
      maxWinners: 1,
      allowMultiplePrizesPerUser: false,
      allowMultipleWinnersPerStore: false
    }
  });

  const selectedCampaign = watch('campaignId');
  const selectedPrizeIds = watch('prizeIds');

  useEffect(() => {
    fetchCampaigns();
  }, []);

  useEffect(() => {
    if (selectedCampaign) {
      fetchPrizesByCampaign(selectedCampaign);
    }
  }, [selectedCampaign]);

  const fetchCampaigns = async () => {
    try {
      const data = await campaignService.getCampaignsByAssociation();
      setCampaigns(data);
    } catch (error) {
      setError('Não foi possível carregar as campanhas. Recarregue a página e tente novamente.');
    }
  };

  const fetchPrizesByCampaign = async (campaignId: string) => {
    try {
      const data = await prizeService.getPrizesByCampaign(campaignId);
      setPrizes(data);
    } catch (error) {
      setError('Não foi possível carregar os prêmios desta campanha. Recarregue a página e tente novamente.');
    }
  };

  const getSelectedPrizes = () => {
    return prizes.filter(prize => selectedPrizeIds.includes(prize.id));
  };

  const onSubmit: SubmitHandler<DrawFormData> = async (data) => {
    setLoading(true);
    setError('');

    const drawDate = new Date(data.drawDate).toISOString();
    const succeeded: string[] = [];
    const failed: { prizeName: string; message: string }[] = [];

    // Criar um sorteio por prêmio selecionado. Cada tentativa é isolada — se
    // uma falhar, as demais ainda são tentadas, e o usuário sabe exatamente
    // quais sorteios foram criados e quais precisam ser refeitos.
    for (const prizeId of data.prizeIds) {
      const prizeName = prizes.find((p) => p.id === prizeId)?.name || prizeId;
      const drawData = {
        campaignId: data.campaignId,
        prizeId: prizeId,
        drawDate: drawDate,
        maxWinners: data.maxWinners,
        allowMultiplePrizesPerUser: data.allowMultiplePrizesPerUser,
        allowMultipleWinnersPerStore: data.allowMultipleWinnersPerStore,
        drawByStore: false, // Sorteio normal (não por loja)
      };

      try {
        await drawService.createDraw(drawData);
        succeeded.push(prizeName);
      } catch (err: any) {
        failed.push({
          prizeName,
          message: err.response?.data?.message || 'Erro desconhecido',
        });
      }
    }

    setLoading(false);

    if (failed.length === 0) {
      navigate('/draws');
      return;
    }

    const failedSummary = failed.map((f) => `${f.prizeName} (${f.message})`).join('; ');
    if (succeeded.length > 0) {
      setError(
        `${succeeded.length} sorteio(s) criado(s) com sucesso, mas ${failed.length} falharam: ${failedSummary}. ` +
          'Os sorteios já criados NÃO serão refeitos automaticamente — crie apenas os que faltam.',
      );
    } else {
      setError(`Nenhum sorteio foi criado. Falhas: ${failedSummary}`);
    }
  };

  return (
    <>
      <AppHeader 
        title="Novo Sorteio" 
        subtitle="Configure um novo sorteio para suas campanhas"
        icon={<Add />}
        backTo="/draws"
      />

      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={2} sx={{ p: 4, borderRadius: 2 }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
            Criar Novo Sorteio
          </Typography>
          
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            Configure os parâmetros do sorteio e selecione os prêmios. Um sorteio será criado para cada prêmio selecionado.
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              {error}
            </Alert>
          )}
          
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={3}>
              <Controller
                name="campaignId"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.campaignId}>
                    <InputLabel>Campanha *</InputLabel>
                    <Select {...field} label="Campanha *" size="small">
                      {campaigns.map((campaign) => (
                        <MenuItem key={campaign.id} value={campaign.id}>
                          {campaign.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.campaignId && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                        {errors.campaignId.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />

              <Controller
                name="prizeIds"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.prizeIds}>
                    <InputLabel>Prêmios *</InputLabel>
                    <Select
                      {...field}
                      multiple
                      label="Prêmios *"
                      disabled={!selectedCampaign}
                      size="small"
                      input={<OutlinedInput label="Prêmios *" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(selected as string[]).map((value) => {
                            const prize = prizes.find(p => p.id === value);
                            return (
                              <Chip 
                                key={value} 
                                label={prize?.name || value}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            );
                          })}
                        </Box>
                      )}
                    >
                      {prizes.map((prize) => (
                        <MenuItem key={prize.id} value={prize.id}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {prize.name}
                            </Typography>
                            <Chip 
                              label={`${prize.quantity} ${prize.quantity === 1 ? 'ganhador' : 'ganhadores'}`}
                              size="small"
                              color="secondary"
                              variant="outlined"
                            />
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.prizeIds && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                        {errors.prizeIds.message}
                      </Typography>
                    )}
                    {!selectedCampaign && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                        Selecione uma campanha primeiro
                      </Typography>
                    )}
                  </FormControl>
                )}
              />

              {/* Preview dos prêmios selecionados */}
              {selectedPrizeIds.length > 0 && (
                <Box sx={{ 
                  p: 3, 
                  borderRadius: 2, 
                  backgroundColor: 'primary.light', 
                  border: '1px solid',
                  borderColor: 'primary.main'
                }}>
                  <Typography variant="h6" gutterBottom sx={{ 
                    color: 'primary.contrastText', 
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}>
                    <EmojiEvents /> Resumo dos Sorteios
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'primary.contrastText', mb: 2 }}>
                    Serão criados <strong>{selectedPrizeIds.length} sorteios</strong>, um para cada prêmio:
                  </Typography>
                  <Stack spacing={1}>
                    {getSelectedPrizes().map((prize, index) => (
                      <Box 
                        key={prize.id}
                        sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          p: 2,
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          borderRadius: 1
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Sorteio {index + 1}: {prize.name}
                        </Typography>
                        <Chip 
                          label={`${prize.quantity} ${prize.quantity === 1 ? 'ganhador' : 'ganhadores'}`}
                          size="small"
                          color="primary"
                        />
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}

              <Controller
                name="drawDate"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Data e Hora do Sorteio *"
                    type="datetime-local"
                    InputLabelProps={{ shrink: true }}
                    error={!!errors.drawDate}
                    helperText={errors.drawDate?.message}
                    size="small"
                  />
                )}
              />

              <Controller
                name="maxWinners"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Quantidade Máxima de Ganhadores *"
                    type="number"
                    inputProps={{ min: 1 }}
                    error={!!errors.maxWinners}
                    helperText={errors.maxWinners?.message || 'Quantidade de ganhadores que cada sorteio pode ter'}
                    size="small"
                    onChange={(e) => field.onChange(parseInt(e.target.value) || 1)}
                  />
                )}
              />

              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                  Configurações dos Sorteios
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Essas configurações serão aplicadas a todos os sorteios criados:
                </Typography>

                <Stack spacing={2}>
                  <Controller
                    name="allowMultiplePrizesPerUser"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Checkbox {...field} checked={field.value} color="primary" />}
                        label={
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              Permitir múltiplos prêmios por usuário
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Um usuário pode ganhar mais de um prêmio entre todos os sorteios
                            </Typography>
                          </Box>
                        }
                      />
                    )}
                  />

                  <Controller
                    name="allowMultipleWinnersPerStore"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Checkbox {...field} checked={field.value} color="primary" />}
                        label={
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              Permitir múltiplos ganhadores por loja
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Cupons da mesma loja podem ter vários ganhadores
                            </Typography>
                          </Box>
                        }
                      />
                    )}
                  />

                </Stack>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, pt: 3 }}>
                <Button 
                  type="submit" 
                  variant="contained" 
                  disabled={loading || selectedPrizeIds.length === 0}
                  sx={{
                    borderRadius: 2,
                    px: 4,
                    py: 1.5,
                    fontWeight: 600,
                    textTransform: 'none',
                    fontSize: '1rem'
                  }}
                >
                  {loading ? '🔄 Criando...' : `🎯 Criar ${selectedPrizeIds.length} ${selectedPrizeIds.length === 1 ? 'Sorteio' : 'Sorteios'}`}
                </Button>
                <Button 
                  variant="outlined" 
                  onClick={() => navigate('/draws')}
                  disabled={loading}
                  sx={{
                    borderRadius: 2,
                    px: 4,
                    py: 1.5,
                    fontWeight: 600,
                    textTransform: 'none'
                  }}
                >
                  Cancelar
                </Button>
              </Box>
            </Stack>
          </form>
        </Paper>
      </Container>
    </>
  );
}; 
export default DrawCreate;